import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { assertNever } from 'game-logic';
import { AlertController } from '@ionic/angular/standalone';
import { GameId } from 'src/app/model/game/game-id';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { UxGameService } from 'src/app/core/services/ux-game/ux-game.service';
import { PaymentService } from 'src/app/core/services/payment/payment.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import {
  ControlMode,
  GameOrchestrationService,
  GameStateEvent,
} from 'src/app/core/services/game-orchestration/game-orchestration.service';
import { GameInterfaceState } from '../controllers/listeners-legacy/ux-game-listener';
import { GameScoreService } from 'src/app/core/services/game-score/game-score.service';
import { UxGameController } from '../controllers/game-legacy/ux-game-controller';
import { GenericSceneController } from '../controllers/scene-controllers/scene-controller-factory';
import { GameUiControlLoaders } from '../game-ui/model/game-ui-controls';
import { GameUiComponent } from '../game-ui/model/game-ui-component.interface';
import { LoadingGameControlsComponent } from '../game-ui/controls/loading-game-controls/loading-game-controls.component';
import { TierFirstControlsComponent } from '../game-ui/controls/tier-first-controls/tier-first-controls.component';
import { ReplayGameControlsComponent } from '../game-ui/controls/replay-game-controls/replay-game-controls.component';
import { GameControlDisplay } from '../game-ui/model/game-control-display';
import { GameScreenComponent } from '../game-ui/game-screen-component/game-screen.component';
import { LoadingSpinnerComponent } from '../../../ui/loading-spinner/loading-spinner.component';
import { PaymentEventData } from 'src/app/shared/types/payment-events';

@Component({
  selector: 'app-game-host',
  templateUrl: './game-host.component.html',
  styleUrls: ['./game-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, GameScreenComponent],
})
export class GameHostComponent implements OnInit, OnDestroy {
  @ViewChild('gameControls', { read: ViewContainerRef, static: false })
  gameControls!: ViewContainerRef;

  @Input() controlMode: ControlMode = 'full';

  orchestration = inject(GameOrchestrationService);

  private analyticsService = inject(AnalyticsService);
  private uxGameService = inject(UxGameService);
  private operatorService = inject(OperatorService);
  private paymentService = inject(PaymentService);
  private alertController = inject(AlertController);

  themeService = inject(ThemeService);
  gameScoreService = inject(GameScoreService);
  GameInterfaceState = GameInterfaceState;

  showingControls = computed(() => {
    const component = this.currentUiComponent();
    return (component?.instance as GameControlDisplay)?.showingControls?.() ?? false;
  });

  isTierFirstActive = signal(false);

  private currentUiComponent = signal<ComponentRef<unknown> | undefined>(undefined);
  private destroy$ = new Subject<void>();
  private loadingSubscriptions: Subscription[] = [];
  private pendingOpenWagerDrawer = false;

  ngOnInit(): void {
    if (this.controlMode === 'full') {
      this.orchestration.stateEvents$.pipe(takeUntil(this.destroy$)).subscribe(async (event) => {
        await this.handleStateEvent(event);
      });

      this.uxGameService.openWagerDrawer$.pipe(takeUntil(this.destroy$)).subscribe(async () => {
        try {
          const instance = this.currentUiComponent()?.instance;
          if (instance instanceof LoadingGameControlsComponent) {
            await instance.openWagerModal();
          } else {
            this.pendingOpenWagerDrawer = true;
          }
        } catch (error) {
          this.analyticsService.handleError(error, 'Open wager drawer');
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearControls();
  }

  private async handleStateEvent(event: GameStateEvent): Promise<void> {
    const { state, controller, gameId } = event;
    const isLegacy = controller instanceof UxGameController;

    switch (state) {
      case GameInterfaceState.GameLoading:
        if (!this.gameScoreService.isPlayingAgain()) {
          this.gameScoreService.clear();
          this.gameScoreService.setGameName(controller.gameName);
          if (this.controlMode === 'full' && !this.operatorService.isGameOnlyOperator()) {
            this.displayLoadingControls(gameId);
          }
        }
        break;
      case GameInterfaceState.GameStarted:
        await this.displayGameControls(gameId, controller);
        break;
      case GameInterfaceState.ReplayReady:
        if (!isLegacy) {
          this.displayReplayControls(controller as GenericSceneController);
        }
        break;
      case GameInterfaceState.GameWaiting:
      case GameInterfaceState.GameStarting:
      case GameInterfaceState.ReplayStarting:
      case GameInterfaceState.ReplayGameInit:
        this.clearControls();
        break;
      default:
        assertNever(state, 'Invalid game interface state');
    }
  }

  private resolveLoadingComponent(
    gameId: GameId,
  ): Type<LoadingGameControlsComponent | TierFirstControlsComponent> {
    if (gameId === GameId.Marbles) {
      // Marbles always has tier-first as it has no loading screen
      this.isTierFirstActive.set(true);
      return TierFirstControlsComponent;
    }

    if (OperatorService.IsDemo) {
      // Demos always have game-first
      this.isTierFirstActive.set(false);
      return LoadingGameControlsComponent;
    }

    const uxMode = this.themeService.playTabConfig().uxMode;

    if (uxMode === 'tier-first') {
      this.isTierFirstActive.set(true);
      return TierFirstControlsComponent;
    }

    this.isTierFirstActive.set(false);
    return LoadingGameControlsComponent;
  }

  private displayLoadingControls(gameId: GameId): void {
    this.clearControls();

    const componentClass = this.resolveLoadingComponent(gameId);
    this.currentUiComponent.set(
      this.gameControls.createComponent(componentClass as Type<LoadingGameControlsComponent>),
    );

    const instance = this.currentUiComponent()?.instance as
      | LoadingGameControlsComponent
      | TierFirstControlsComponent
      | undefined;
    if (!instance) {
      return;
    }

    instance.gameId = gameId;

    if (this.pendingOpenWagerDrawer && instance instanceof LoadingGameControlsComponent) {
      instance.autoOpenWagerDrawer = true;
      this.pendingOpenWagerDrawer = false;
    }

    this.loadingSubscriptions.push(
      instance.launchPracticeEvent.subscribe(() => {
        this.uxGameService.launchPracticeGame(0, 0);
      }),
    );

    this.loadingSubscriptions.push(
      instance.launchLocalEvent.subscribe(async () => {
        await this.launchLocalGame();
      }),
    );

    this.loadingSubscriptions.push(
      instance.showPaymentEvent.subscribe(async (data: PaymentEventData) => {
        try {
          const result = await this.paymentService.showTierPaymentDrawer(gameId, data);
          if (
            !result.dropped &&
            result.role === 'changePrizeAmount' &&
            instance instanceof LoadingGameControlsComponent
          ) {
            await instance.openWagerModal();
          }
        } catch (error) {
          this.analyticsService.handleError(error, 'Show payment');
        }
      }),
    );
  }

  private async displayGameControls(
    gameId: GameId,
    gameController: UxGameController | GenericSceneController,
  ): Promise<void> {
    try {
      const componentClass = await GameUiControlLoaders[gameId]();

      this.clearControls();
      this.currentUiComponent.set(this.gameControls.createComponent(componentClass));

      const instance = this.currentUiComponent()?.instance as
        | GameUiComponent<UxGameController | GenericSceneController>
        | undefined;
      if (instance) {
        instance.gameController = gameController;
      }
    } catch (error) {
      this.analyticsService.handleError(error, 'Load game UI');
      throw error;
    }
  }

  private displayReplayControls(gameController: GenericSceneController): void {
    this.clearControls();
    this.currentUiComponent.set(this.gameControls.createComponent(ReplayGameControlsComponent));
    const instance = this.currentUiComponent()?.instance as
      | GameUiComponent<GenericSceneController>
      | undefined;
    if (instance) {
      instance.gameController = gameController;
    }
  }

  private clearControls(): void {
    this.loadingSubscriptions.forEach((sub) => sub.unsubscribe());
    this.loadingSubscriptions = [];

    this.currentUiComponent()?.destroy();
    this.currentUiComponent.set(undefined);
    this.gameControls?.clear();
    this.isTierFirstActive.set(false);
  }

  private async launchLocalGame(): Promise<void> {
    const alert = await this.alertController.create({
      cssClass: 'alert',
      header: 'Enter a seed number',
      inputs: [
        {
          name: 'number',
          type: 'number',
          placeholder: 'Seed number',
        },
      ],
      buttons: [
        {
          cssClass: 'alert-button',
          text: 'OK',
          handler: (data) => {
            const num = parseInt(data.number);
            this.uxGameService.launchLocalGame(Number.isFinite(num) ? num : undefined);
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    await alert.onDidDismiss();
  }
}
